import './leasing-approved-item.scss';

import { useMemo, useState } from 'react';

import type { TLeasingOrderResponse } from '@/components/App/Leasing/types';
import { OfferCard } from '@/components/Shared/ui';
import { SYMBOLS } from '@/constants';
import { useWindowSize } from '@/hooks/useWindowSize';
import { formatPrice } from '@/services/formatter';
import { useAppSelector } from '@/store';

import { getLeasingOfferBodyProducts } from '../../helpers';
import { useLeasingHeaderActions } from '../../hooks';
import { LeasingDetails } from '../../ui';

type TLeasingApprovedItemsProps = {
  leasingOffer: TLeasingOrderResponse;
};

const LeasingApprovedItem = ({ leasingOffer }: TLeasingApprovedItemsProps) => {
  const [windowWidth] = useWindowSize();
  const mobileAlternatives = windowWidth <= 860;

  const { request } = useAppSelector((state) => state.ieLeasingOrder);

  const [isLeasingDetailsModalOpen, setIsLeasingDetailsModalOpen] =
    useState(false);

  const { headerActions } = useLeasingHeaderActions({
    leasingOffer
  });

  const additionalProducts = useMemo(() => {
    const products: React.ComponentProps<
      typeof OfferCard.Body
    >['additionalProducts'] = {
      title: 'Доп. продукты',
      content: []
    };

    if (leasingOffer.insuranceAmount) {
      products.content.push(
        `КАСКО ${SYMBOLS.longDash} ${formatPrice(leasingOffer.insuranceAmount)} ${SYMBOLS.tenge}`
      );
    }
    if (leasingOffer.telematicsAmount) {
      products.content.push(
        `Телематика ${SYMBOLS.longDash} ${formatPrice(leasingOffer.telematicsAmount)} ${SYMBOLS.tenge}`
      );
    }

    return products;
  }, [leasingOffer]);

  const isLeasingFinanced: boolean = useMemo(() => {
    if (!request) return false;
    return request.responses.some(
      (offer) =>
        offer.status.code === 'financed' ||
        offer.status.code === 'waiting_of_financing'
    );
  }, [request]);

  const isSelected: boolean = useMemo(
    () =>
      leasingOffer.status.code === 'financed' ||
      leasingOffer.status.code === 'waiting_of_financing',
    [leasingOffer.status.code]
  );

  const headerTitle: string = leasingOffer.leasingCompany.name;
  const statusText: string = leasingOffer.status.name;

  const status =
    leasingOffer.status.code === 'cancelled' ||
    leasingOffer.status.code === 'declined' ||
    leasingOffer.status.code === 'error'
      ? 'disabled'
      : leasingOffer.status.code === 'waiting_bank_response' ||
          leasingOffer.status.code === 'waiting_of_financing'
        ? 'processing'
        : 'success';

  return (
    <>
      <OfferCard
        className="ie-leasing-item"
        status={status}
        isSelected={isSelected}
        statusText={statusText}
      >
        <OfferCard.Header
          title={headerTitle}
          icon={leasingOffer.leasingCompany.icon}
          headerActions={headerActions}
          info={
            isLeasingFinanced && !isSelected
              ? 'Есть принятое предложение'
              : undefined
          }
          descriptionAction={
            leasingOffer.comment
              ? {
                  onClick: () => setIsLeasingDetailsModalOpen(true),
                  text: <span className="ie-offer-step__hidden">Описание</span>
                }
              : undefined
          }
        />

        <OfferCard.Body
          footerActions={headerActions}
          products={getLeasingOfferBodyProducts({
            leasingOffer,
            mobileAlternatives,
            request
          })}
          additionalProducts={additionalProducts}
        />
      </OfferCard>

      <LeasingDetails
        isModalOpen={isLeasingDetailsModalOpen}
        onClose={() => setIsLeasingDetailsModalOpen(false)}
        leasingOffer={leasingOffer}
      />
    </>
  );
};

export default LeasingApprovedItem;
